/**
 * Wallet Store
 *
 * Manages wallet connection, balance, and transaction history.
 * Real-time updates via WebSocket events.
 *
 * MIGRATED TO USE GENERATED OPENAPI TYPES
 */

import { defineStore } from 'pinia'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNodeStore } from './node'
import { useNetworkStore } from './network'
import { api } from '@/api/client'
import type { components } from '@/types/api'

// Use auto-generated types from OpenAPI spec
type WalletBalance = components['schemas']['WalletBalanceResponse']
type TransactionEntry = components['schemas']['TransactionEntry']

// Frontend-specific type for local transaction state
interface Transaction {
  from: string
  to: string
  amount: number
  timestamp: Date
  txHash: string
  status: 'pending' | 'confirmed' | 'failed'
}

export const useWalletStore = defineStore('wallet', () => {
  // State
  const address = ref<string | null>(null)
  const publicKey = ref<string | null>(null)
  const balance = ref<{
    total: number
    available: number
    locked: number
  }>({
    total: 0,
    available: 0,
    locked: 0
  })
  const transactions = ref<Transaction[]>([])
  const isConnecting = ref(false)
  const isRefreshing = ref(false)
  const lastError = ref<string | null>(null)

  // Get node store
  const nodeStore = useNodeStore()

  // Computed
  const isConnected = computed(() => !!address.value)
  const formattedAddress = computed(() => {
    if (!address.value) return ''
    return `${address.value.slice(0, 6)}...${address.value.slice(-4)}`
  })

  // Actions
  const connect = async () => {
    if (!nodeStore.connectedNode) {
      throw new Error('Please connect to a node first')
    }

    isConnecting.value = true
    lastError.value = null

    try {
      // Get wallet info from the connected node
      const response = await fetch(`${nodeStore.apiUrl}/wallet/info`)
      if (!response.ok) {
        throw new Error('Failed to get wallet info')
      }

      const walletInfo = await response.json()
      address.value = walletInfo.address
      publicKey.value = walletInfo.public_key

      // Load initial balance
      await refreshBalance()

      // Load initial transactions
      await refreshTransactions()

      // Start polling for balance updates
      startBalancePolling()

      return walletInfo
    } catch (error) {
      console.error('Failed to connect wallet:', error)
      lastError.value = error instanceof Error ? error.message : 'Connection failed'
      throw error
    } finally {
      isConnecting.value = false
    }
  }

  const disconnect = () => {
    address.value = null
    publicKey.value = null
    balance.value = {
      total: 0,
      available: 0,
      locked: 0
    }
    transactions.value = []
    stopBalancePolling()
  }

  const refreshBalance = async () => {
    if (!address.value) return

    isRefreshing.value = true

    try {
      const response = await api.getWalletBalance(address.value)
      const balanceData = response.data

      balance.value = {
        total: balanceData.balance || balanceData.total || 0,
        available: balanceData.available || 0,
        locked: balanceData.locked || 0
      }
    } catch (error) {
      console.error('Failed to refresh balance:', error)
      lastError.value = error instanceof Error ? error.message : 'Balance refresh failed'
    } finally {
      isRefreshing.value = false
    }
  }

  const transfer = async (to: string, amount: number) => {
    if (!address.value || !nodeStore.connectedNode) {
      throw new Error('Wallet not connected')
    }

    if (amount > balance.value.available) {
      throw new Error('Insufficient balance')
    }

    try {
      const response = await fetch(`${nodeStore.apiUrl}/wallet/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from: address.value,
          to,
          amount,
          signature: 'placeholder' // TODO: Implement actual signature
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Transfer failed')
      }

      const result = await response.json()

      // Add to transactions
      transactions.value.unshift({
        from: address.value,
        to,
        amount,
        timestamp: new Date(),
        txHash: result.tx_hash,
        status: 'confirmed'
      })

      // Refresh balance
      await refreshBalance()

      return result
    } catch (error) {
      console.error('Transfer failed:', error)
      throw error
    }
  }

  const requestFromFaucet = async () => {
    if (!address.value || !nodeStore.connectedNode) {
      throw new Error('Wallet not connected')
    }

    try {
      const response = await fetch(`${nodeStore.apiUrl}/wallet/faucet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          address: address.value
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Faucet request failed')
      }

      const result = await response.json()

      // Add to transactions
      transactions.value.unshift({
        from: 'faucet',
        to: address.value,
        amount: result.amount,
        timestamp: new Date(),
        txHash: result.tx_hash,
        status: 'confirmed'
      })

      // Refresh balance
      await refreshBalance()

      return result
    } catch (error) {
      console.error('Faucet request failed:', error)
      throw error
    }
  }

  const refreshTransactions = async () => {
    if (!isConnected.value) return

    isRefreshing.value = true
    lastError.value = null

    try {
      // Fetch recent transactions from the explorer backend
      const response = await api.getWalletTransactions(address.value)
      const data = response.data

      // Transform the transactions to match our expected format
      transactions.value = (data.transactions || []).map((tx: any) => ({
        from: tx.from,
        to: tx.to,
        amount: tx.amount,
        timestamp: new Date(tx.timestamp),
        txHash: tx.tx_hash || tx.txHash,
        status: tx.status || 'confirmed'
      }))
    } catch (error) {
      console.error('Failed to refresh transactions:', error)
      // Don't overwrite existing transactions on error
    } finally {
      isRefreshing.value = false
    }
  }

  // Balance polling
  let balanceInterval: NodeJS.Timeout | null = null

  const startBalancePolling = () => {
    stopBalancePolling()
    balanceInterval = setInterval(() => {
      refreshBalance()
    }, 10000) // Poll every 10 seconds
  }

  const stopBalancePolling = () => {
    if (balanceInterval) {
      clearInterval(balanceInterval)
      balanceInterval = null
    }
  }

  // Listen for new transactions from WebSocket
  const handleNewTransaction = (event: Event) => {
    const customEvent = event as CustomEvent
    const transaction = customEvent.detail

    // Only add if it involves our wallet
    if (address.value && (transaction.from === address.value || transaction.to === address.value)) {
      // Add to transactions array at the beginning
      transactions.value.unshift({
        from: transaction.from,
        to: transaction.to,
        amount: transaction.amount,
        timestamp: new Date(transaction.timestamp),
        txHash: transaction.tx_hash,
        status: transaction.status || 'confirmed'
      })

      // Keep only last 100 transactions
      if (transactions.value.length > 100) {
        transactions.value = transactions.value.slice(0, 100)
      }

      // Refresh balance since we have a new transaction
      refreshBalance()
    }
  }

  // Set up event listener when store is created
  if (typeof window !== 'undefined') {
    window.addEventListener('new-transaction', handleNewTransaction)
  }

  return {
    // State
    address,
    publicKey,
    balance,
    transactions,
    isConnecting,
    isRefreshing,
    lastError,

    // Computed
    isConnected,
    formattedAddress,

    // Actions
    connect,
    disconnect,
    refreshBalance,
    refreshTransactions,
    transfer,
    requestFromFaucet,
    startBalancePolling,
    stopBalancePolling
  }
})